import { logger } from '../../utils/logger';
import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, EyeIcon, PhoneIcon, MailIcon, MapPinIcon, FileTextIcon, CalendarIcon, DownloadIcon, UsersIcon, ShieldIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, ClipboardListIcon, ChevronDownIcon, ChevronUpIcon, StethoscopeIcon, ActivityIcon, PillIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../contexts/ToastContext';
import { Patient, Gender } from '../../types';
import { formatDate, formatTime, getGenderName, getStatusName, getDoctorPrefix } from '../../utils/helpers';
import { validatePatientForm } from '../../utils/validation';
import { exportPatientsToExcel } from '../../utils/excelExport';
import { exportPatientsToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Patients({
  autoOpenNew
}: {
  autoOpenNew?: boolean;
}) {
  const {
    patients,
    addPatient,
    updatePatient,
    deletePatient,
    getPatient,
    insurances,
    getInsurance,
    getAppointmentsByPatient,
    getDoctor,
    getService,
    getSpecialty,
    doctors,
    appointments,
    getClinicalHistoriesByPatient
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly,
    canView,
    permissions
  } = usePermissions();
  const readOnly = isReadOnly('patients');
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  // Check if user can see insurance field
  const canSeeInsurance = canView('patients_insurance');
  // Find the doctor record that matches the current user (by email)
  const currentUserDoctor = doctors.find(d => d.email?.toLowerCase() === user?.email?.toLowerCase());
  const isDoctorUser = !!currentUserDoctor && !isAdmin;
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Expanded clinical history entries
  const [expandedVisitId, setExpandedVisitId] = useState<string | null>(null);
  // Clinical history statuses (read from same localStorage as ClinicalHistory module)
  const CH_STATUS_KEY = 'savidna-clinical-history-status';
  const [chStatuses, setChStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CH_STATUS_KEY);
      if (saved) setChStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  // Listen for storage changes (in case ClinicalHistory module updates statuses)
  useEffect(() => {
    const handleStorage = () => {
      try {
        const saved = localStorage.getItem(CH_STATUS_KEY);
        if (saved) setChStatuses(JSON.parse(saved));
      } catch {}
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const getClinicalHistoryStatus = (id: string): 'active' | 'archived' | 'deactivated' => chStatuses[id] || 'active';
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-patients-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    // Persist to Supabase
    updatePatient(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving patient in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    // Persist to Supabase
    updatePatient(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating patient in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    // Persist to Supabase
    updatePatient(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring patient in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    gender: 'masculino' as Gender,
    phone: '',
    email: '',
    address: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    insuranceId: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Filter patients: doctors only see patients they have appointments with
  const accessiblePatients = isDoctorUser && currentUserDoctor ? patients.filter(p => appointments.some(a => a.patientId === p.id && a.doctorId === currentUserDoctor.id)) : patients;
  const filteredPatients = accessiblePatients.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.email?.toLowerCase().includes(searchTerm.toLowerCase()) || p.phone?.includes(searchTerm) || p.cedula && p.cedula.includes(searchTerm);
    const status = getItemStatus(p.id);
    return matchesSearch && status === viewMode;
  });
  const {
    sortedItems: sortedPatients,
    requestSort,
    isSorted
  } = useSortableData(filteredPatients);
  const {
    paginatedItems: paginatedPatients,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(sortedPatients);
  // Counts for tabs
  const activeCount = accessiblePatients.filter(p => getItemStatus(p.id) === 'active').length;
  const archivedCount = accessiblePatients.filter(p => getItemStatus(p.id) === 'archived').length;
  const deactivatedCount = accessiblePatients.filter(p => getItemStatus(p.id) === 'deactivated').length;
  const genderOptions = [{
    value: 'masculino',
    label: 'Masculino'
  }, {
    value: 'femenino',
    label: 'Femenino'
  }, {
    value: 'otro',
    label: 'Otro'
  }];
  const insuranceOptions = [{
    value: '',
    label: 'Sin seguro'
  }, ...insurances.map(i => ({
    value: i.id,
    label: i.name
  }))];
  // Format Cédula as user types
  const handleCedulaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 11) value = value.slice(0, 11); // Limit to 11 digits
    // Format: 000-0000000-0
    let formatted = value;
    if (value.length > 3) {
      formatted = value.slice(0, 3) + '-' + value.slice(3);
    }
    if (value.length > 10) {
      formatted = formatted.slice(0, 11) + '-' + formatted.slice(11);
    }
    setFormData(prev => ({
      ...prev,
      cedula: formatted
    }));
  };
  const openNewForm = () => {
    if (!canCreate('patients')) return;
    setSelectedPatient(null);
    setFormData({
      name: '',
      gender: 'masculino',
      phone: '',
      email: '',
      address: '',
      emergencyContactName: '',
      emergencyContactRelationship: '',
      emergencyContactPhone: '',
      insuranceId: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };

  // Auto-open new patient form when navigated with :new param
  // setTimeout ensures the module is fully mounted before opening the modal
  useEffect(() => {
    if (autoOpenNew && permissions) {
      const timer = setTimeout(() => openNewForm(), 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew, permissions]);
  const openEditForm = (patient: Patient) => {
    if (!canEdit('patients')) return;
    setSelectedPatient(patient);
    setFormData({
      name: patient.name,
      gender: patient.gender,
      phone: patient.phone || '',
      email: patient.email || '',
      address: patient.address || '',
      emergencyContactName: patient.emergencyContact?.name || '',
      emergencyContactRelationship: patient.emergencyContact?.relationship || '',
      emergencyContactPhone: patient.emergencyContact?.phone || '',
      insuranceId: patient.insuranceId || ''
    });
    setFormErrors({});
    setIsViewOpen(false);
    setIsFormOpen(true);
  };
  const openView = (patient: Patient) => {
    setSelectedPatient(patient);
    // Re-read clinical history statuses when opening view
    try {
      const saved = localStorage.getItem(CH_STATUS_KEY);
      if (saved) setChStatuses(JSON.parse(saved));
    } catch {}
    setActiveTab('info');
    setIsViewOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validatePatientForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const patientData = {
      name: capitalizeName(formData.name),
      gender: formData.gender,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      emergencyContact: {
        name: capitalizeName(formData.emergencyContactName),
        relationship: formData.emergencyContactRelationship,
        phone: formData.emergencyContactPhone
      },
      insuranceId: formData.insuranceId || null
    };
    try {
      if (selectedPatient) {
        await updatePatient(selectedPatient.id, patientData);
        showToast({
          type: 'success',
          title: 'Paciente actualizado',
          message: `${patientData.name} fue actualizado correctamente.`
        });
      } else {
        await addPatient(patientData);
        showToast({
          type: 'success',
          title: 'Paciente creado',
          message: `${patientData.name} fue registrado correctamente.`
        });
      }
      setIsFormOpen(false);
    } catch (error: any) {
      logger.error('Error guardando paciente:', error);
      showToast({
        type: 'error',
        title: 'Error al guardar',
        message: error?.message || 'No se pudo guardar el paciente.'
      });
      setFormErrors({
        general: error?.message || 'Error al guardar el paciente'
      });
    }
  };
  const openActionConfirm = (patient: Patient, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedPatient(patient);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedPatient || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedPatient.id);
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedPatient.id);
    } else if (actionType === 'delete') {
      deletePatient(selectedPatient.id);
      setIsActionConfirmOpen(false);
      setIsViewOpen(false);
    }
  };
  const saveMedicalHistory = async () => {
    if (readOnly) return;
    if (selectedPatient && medicalData) {
      setIsSavingMedical(true);
      setMedicalSaveError('');
      setMedicalSaveSuccess(false);
      try {
        await updateMedicalHistory(selectedPatient.id, medicalData);
        setMedicalSaveSuccess(true);
        setTimeout(() => setMedicalSaveSuccess(false), 3000);
      } catch (error: any) {
        logger.error('❌ Error guardando historial médico:', error);
        setMedicalSaveError(error?.message || 'Error al guardar el historial médico');
      } finally {
        setIsSavingMedical(false);
      }
    }
  };
  const tabs = [{
    id: 'info',
    label: 'Información',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-3752" />
  }, {
    id: 'visits',
    label: 'Registro de Visitas',
    icon: <ClipboardListIcon className="w-4 h-4" data-id="element-3753" />
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-3754" />
  }];
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = patients;
    if (dateFrom && dateTo) {
      dataToExport = patients.filter(p => {
        const dateStr = new Date(p.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportPatientsToExcel(dataToExport, (insuranceId: string | null) => {
      if (!insuranceId) return 'Sin seguro';
      const insurance = getInsurance(insuranceId);
      return insurance?.name || 'Sin seguro';
    });
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = patients;
    if (dateFrom && dateTo) {
      dataToExport = patients.filter(p => {
        const dateStr = new Date(p.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportPatientsToPdf(dataToExport, (insuranceId: string | null) => {
      if (!insuranceId) return 'Sin seguro';
      const insurance = getInsurance(insuranceId);
      return insurance?.name || 'Sin seguro';
    });
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  // Helper to capitalize each word in a name
  const capitalizeName = (name: string) => {
    return name.trim().split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  return <div className="space-y-6" data-id="element-3755">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 shadow-xl text-white" data-id="element-3756">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(ellipse at center, transparent 30%, rgba(255,255,255,1) 31%, rgba(255,255,255,1) 32%, transparent 33%), radial-gradient(ellipse at center, transparent 60%, rgba(255,255,255,1) 61%, rgba(255,255,255,1) 62%, transparent 63%)',
        backgroundSize: '48px 48px'
      }} data-id="element-3757" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-3758" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-3759" />
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 65%)'
      }} data-id="element-3760" />
        <div className="absolute -bottom-16 -left-8 w-64 h-64 rounded-full opacity-15 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(6,182,212,0.8) 0%, transparent 60%)'
      }} data-id="element-3761" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-3762" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-3763">
          <div data-id="element-3764">
            <div className="flex items-center gap-3 mb-2" data-id="element-3765">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-3766">
                <UsersIcon className="w-6 h-6 text-white" data-id="element-3767" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-3768">Pacientes</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-3769">
              {isDoctorUser ? 'Mis pacientes — Solo se muestran pacientes con citas asignadas a usted' : 'Gestión de pacientes y expedientes médicos'}
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-3770">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-3771">
            {canExportPdf && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-3773" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-3772">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-3775" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-3774">
                Exportar Excel
              </Button>}
            {canCreate('patients') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3777" />} onClick={openNewForm} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-3776">
                Nuevo Paciente
              </Button>}
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-3778">
        <div className="w-full md:w-96 relative" data-id="element-3779">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-3780" />
          <Input style={{
          paddingLeft: "2.5rem"
        }} placeholder="Buscar por nombre, cédula, correo o teléfono..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-3781" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-3782">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-3783">
              <tab.icon className="w-4 h-4" data-id="element-3784" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-3785">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {/* Patients List */}
      {/* ── MOBILE CARD LIST ── */}
      <div className="sm:hidden space-y-0 divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden" data-id="element-3786">
        {paginatedPatients.length === 0 ? <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-3787">
          <UsersIcon className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" data-id="element-3788" />
          <p className="text-sm" data-id="element-3789">{searchTerm ? 'No hay resultados' : 'No hay pacientes'}</p>
        </div> : paginatedPatients.map(patient => <div key={patient.id} className="px-4 py-3 flex items-center gap-3" data-id="element-3790">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-3791">
              {patient.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0" data-id="element-3792">
              <p className="font-semibold text-gray-900 dark:text-white text-sm truncate capitalize" data-id="element-3793">{patient.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-3794">{patient.phone} · {patient.email}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3795">
              <button onClick={() => openView(patient)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Ver" data-id="element-3796"><EyeIcon className="w-4 h-4" data-id="element-3797" /></button>
              {viewMode === 'active' && canEdit('patients') && <button onClick={() => openEditForm(patient)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-3798"><EditIcon className="w-4 h-4" data-id="element-3799" /></button>}
            </div>
          </div>)}
        {/* Mobile pagination */}
        <div className="px-4 pb-3 pt-2 border-t border-gray-100 dark:border-gray-800" data-id="element-3800">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-3801" />
        </div>
      </div>

      <div className="hidden sm:block" data-id="element-3802">
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-3803">
        <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-3804" />
        <div className="overflow-x-auto" data-id="element-3805">
          <table className="w-full min-w-[600px]" data-id="element-3806">
            <thead data-id="element-3807">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-3808">
                <SortableHeader label="Paciente" sortKey="name" currentDirection={isSorted('name')} onSort={requestSort} data-id="element-3809" />
                <SortableHeader label="Contacto" sortKey="phone" currentDirection={isSorted('phone')} onSort={requestSort} data-id="element-3810" />
                <SortableHeader label="Dirección" sortKey="address" currentDirection={isSorted('address')} onSort={requestSort} className="hidden sm:table-cell" data-id="element-3811" />
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3812">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3813">
              {filteredPatients.length === 0 ? <tr data-id="element-3814">
                  <td colSpan={4} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-3815">
                    <div className="flex flex-col items-center justify-center" data-id="element-3816">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-3817">
                        <UsersIcon className="w-8 h-8 text-gray-400" data-id="element-3818" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-3819">
                        No se encontraron pacientes
                      </h3>
                      <p className="text-sm" data-id="element-3820">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza creando un nuevo paciente' : 'No hay pacientes en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : paginatedPatients.map(patient => {
                return <tr key={patient.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-3821">
                      <td className="py-4 px-6" data-id="element-3822">
                        <div className="flex items-center gap-3" data-id="element-3823">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold shadow-sm" data-id="element-3824">
                            {patient.name.charAt(0).toUpperCase()}
                          </div>
                          <div data-id="element-3825">
                            <p className="font-medium text-gray-900 dark:text-white capitalize" data-id="element-3826">
                              {patient.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3827">
                              {getGenderName(patient.gender)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-3828">
                        <div className="space-y-1" data-id="element-3829">
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1.5" data-id="element-3830">
                            <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3831" />{' '}
                            {patient.phone}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5" data-id="element-3832">
                            <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3833" />{' '}
                            {patient.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400 hidden sm:table-cell" data-id="element-3834">
                        {patient.address || '-'}
                      </td>
                      <td className="py-4 px-6" data-id="element-3835">
                        <div className="flex items-center justify-end gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-3836">
                          <button onClick={() => openView(patient)} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Ver detalles" data-id="element-3837">
                            <EyeIcon className="w-4 h-4" data-id="element-3838" />
                          </button>

                          {viewMode === 'active' ? <>
                              {canEdit('patients') && <button onClick={() => openEditForm(patient)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-3839">
                                  <EditIcon className="w-4 h-4" data-id="element-3840" />
                                </button>}
                              {canDelete('patients') && <>
                                  <button onClick={() => openActionConfirm(patient, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-3841">
                                    <ArchiveIcon className="w-4 h-4" data-id="element-3842" />
                                  </button>
                                  <button onClick={() => openActionConfirm(patient, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-3843">
                                    <PowerOffIcon className="w-4 h-4" data-id="element-3844" />
                                  </button>
                                </>}
                            </> : <button onClick={() => restoreItem(patient.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-3845">
                              <RotateCcwIcon className="w-4 h-4" data-id="element-3846" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-3847">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-3848" />
        </div>
      </Card>

      {/* Patient Form Modal */}
      </div>

      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedPatient ? 'Editar Paciente' : 'Nuevo Paciente'} size="lg" icon={<UsersIcon className="w-5 h-5" data-id="element-3850" />} accentColor="emerald" data-id="element-3849">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3851">
          {/* General Error Message */}
          {formErrors.general && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400 font-medium" data-id="element-3852">
              {formErrors.general}
            </div>}
          {/* Personal Info Section */}
          <div data-id="element-3853">
            <div className="flex items-center gap-2 mb-4" data-id="element-3854">
              <span className="w-1.5 h-5 rounded-full bg-emerald-500" data-id="element-3855"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3856">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3857">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3858">
                <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} error={formErrors.name} placeholder="Nombre del paciente" data-id="element-3859" />
                <Select label="Sexo" options={genderOptions} value={formData.gender} onChange={value => setFormData({
                ...formData,
                gender: value as Gender
              })} error={formErrors.gender} data-id="element-3860" />
              </div>

              <Input label="Dirección" value={formData.address} onChange={e => setFormData({
              ...formData,
              address: e.target.value
            })} placeholder="Dirección completa" data-id="element-3861" />
            </div>
          </div>

          {/* Contact Section */}
          <div data-id="element-3862">
            <div className="flex items-center gap-2 mb-4" data-id="element-3863">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-3864"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3865">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3866">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3867">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} error={formErrors.phone} placeholder="809-000-0000" data-id="element-3868" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="paciente@email.com" data-id="element-3869" />
              </div>
            </div>
          </div>

          {/* Emergency Contact Section */}
          <div data-id="element-3870">
            <div className="flex items-center gap-2 mb-4" data-id="element-3871">
              <span className="w-1.5 h-5 rounded-full bg-rose-500" data-id="element-3872"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3873">
                Contacto de Emergencia
              </h3>
            </div>
            <div className="bg-red-50/50 dark:bg-red-900/5 rounded-xl p-4 space-y-4 border border-red-100 dark:border-red-900/20" data-id="element-3874">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3875">
                <Input label="Nombre Completo" value={formData.emergencyContactName} onChange={e => setFormData({
                ...formData,
                emergencyContactName: e.target.value
              })} placeholder="Nombre del contacto" data-id="element-3876" />
                <Input label="Parentesco" value={formData.emergencyContactRelationship} onChange={e => setFormData({
                ...formData,
                emergencyContactRelationship: e.target.value
              })} placeholder="Ej: Madre, Esposo" data-id="element-3877" />
              </div>
              <Input label="Teléfono de Emergencia" value={formData.emergencyContactPhone} onChange={e => setFormData({
              ...formData,
              emergencyContactPhone: e.target.value
            })} error={formErrors.emergencyContactPhone} placeholder="Número de contacto" data-id="element-3878" />
            </div>
          </div>

          {/* Insurance Section - Only visible with patients_insurance permission */}
          {canSeeInsurance && <div data-id="element-3879">
              <div className="flex items-center gap-2 mb-4" data-id="element-3880">
                <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-3881"></span>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3882">
                  Seguro Médico
                </h3>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3883">
                <Select label="Seguro Médico" options={insuranceOptions} value={formData.insuranceId} onChange={value => setFormData({
              ...formData,
              insuranceId: value
            })} data-id="element-3884" />
              </div>
            </div>}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 modal-actions" data-id="element-3885">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-3886">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3887">
              {selectedPatient ? 'Guardar Cambios' : 'Crear Paciente'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Patient View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Expediente del Paciente" size="xl" data-id="element-3888">
        {selectedPatient && <div className="space-y-4" data-id="element-3889">
            {/* Hero Header */}
            <div className="relative rounded-xl overflow-hidden bg-gradient-to-r from-teal-500 to-cyan-500 shadow-sm" data-id="element-3890">
              <div className="px-4 py-4" data-id="element-3891">
                <div className="flex items-center gap-3" data-id="element-3892">
                  <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl font-bold text-white shadow-sm flex-shrink-0" data-id="element-3893">
                    {selectedPatient.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3894">
                    <h3 className="text-lg font-bold text-white capitalize leading-tight" data-id="element-3895">
                      {selectedPatient.name}
                    </h3>
                    <p className="text-sm font-medium text-white/80 capitalize" data-id="element-3896">
                      {getGenderName(selectedPatient.gender)}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0" data-id="element-3897">
                    {!readOnly && viewMode === 'active' && <>
                        {canEdit('patients') && <Button variant="outline" size="sm" onClick={() => openEditForm(selectedPatient)} className="bg-white/20 border-white/30 text-white hover:bg-white/30 backdrop-blur-sm" data-id="element-3898">
                            <EditIcon className="w-4 h-4 mr-1" data-id="element-3899" /> Editar
                          </Button>}
                        {canDelete('patients') && <Button variant="danger" size="sm" onClick={() => openActionConfirm(selectedPatient, 'archive')} className="bg-white/20 border-white/30 text-white hover:bg-red-500/60 backdrop-blur-sm" data-id="element-3900">
                            <ArchiveIcon className="w-4 h-4" data-id="element-3901" />
                          </Button>}
                      </>}
                  </div>
                </div>
              </div>
            </div>

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-3902">
              <TabPanel id="info" activeTab={activeTab} data-id="element-3903">
                <div className="space-y-3" data-id="element-3904">
                  {/* Contact Info - compact rows */}
                  <div className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700" data-id="element-3905">
                    {/* Phone */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800" data-id="element-3906">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0" data-id="element-3907">
                        <PhoneIcon className="w-4 h-4 text-blue-500" data-id="element-3908" />
                      </div>
                      <div className="min-w-0" data-id="element-3909">
                        <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" data-id="element-3910">Teléfono</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-3911">
                          {selectedPatient.phone || <span className="text-gray-400 font-normal" data-id="element-3912">Sin teléfono</span>}
                        </p>
                      </div>
                    </div>
                    {/* Email */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800" data-id="element-3913">
                      <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0" data-id="element-3914">
                        <MailIcon className="w-4 h-4 text-purple-500" data-id="element-3915" />
                      </div>
                      <div className="min-w-0" data-id="element-3916">
                        <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" data-id="element-3917">Correo</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-3918">
                          {selectedPatient.email || <span className="text-gray-400 font-normal" data-id="element-3919">Sin correo</span>}
                        </p>
                      </div>
                    </div>
                    {/* Address */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800" data-id="element-3920">
                      <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0" data-id="element-3921">
                        <MapPinIcon className="w-4 h-4 text-orange-500" data-id="element-3922" />
                      </div>
                      <div className="min-w-0" data-id="element-3923">
                        <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" data-id="element-3924">Dirección</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3925">
                          {(() => {
                        const a = selectedPatient.address;
                        if (!a || typeof a !== 'string') return <span className="text-gray-400 font-normal" data-id="element-3926">Sin dirección</span>;
                        const trimmed = a.trim();
                        if (!trimmed || trimmed === '{}' || trimmed === '[]' || trimmed === 'null') return <span className="text-gray-400 font-normal" data-id="element-3927">Sin dirección</span>;
                        return trimmed;
                      })()}
                        </p>
                      </div>
                    </div>
                    {/* Insurance */}
                    {canSeeInsurance && <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800" data-id="element-3928">
                        <div className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0" data-id="element-3929">
                          <ShieldIcon className="w-4 h-4 text-teal-500" data-id="element-3930" />
                        </div>
                        <div className="min-w-0" data-id="element-3931">
                          <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" data-id="element-3932">Seguro Médico</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-3933">
                            {selectedPatient.insuranceId ? getInsurance(selectedPatient.insuranceId)?.name : <span className="text-gray-400 font-normal" data-id="element-3934">Sin seguro</span>}
                          </p>
                        </div>
                      </div>}
                  </div>

                  {/* Emergency Contact */}
                  <div className="rounded-xl border border-red-100 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10 overflow-hidden" data-id="element-3935">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-red-100 dark:border-red-900/30" data-id="element-3936">
                      <span className="w-2 h-5 rounded-full bg-red-500 flex-shrink-0" data-id="element-3937"></span>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-3938">Contacto de Emergencia</h3>
                    </div>
                    <div className="divide-y divide-red-100 dark:divide-red-900/20" data-id="element-3939">
                      <div className="flex items-center gap-3 px-4 py-3" data-id="element-3940">
                        <UsersIcon className="w-4 h-4 text-red-400 flex-shrink-0" data-id="element-3941" />
                        <div className="min-w-0" data-id="element-3942">
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide" data-id="element-3943">Nombre</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3944">
                            {selectedPatient.emergencyContact?.name || <span className="text-gray-400 font-normal" data-id="element-3945">No registrado</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 px-4 py-3" data-id="element-3946">
                        <FileTextIcon className="w-4 h-4 text-red-400 flex-shrink-0" data-id="element-3947" />
                        <div className="min-w-0" data-id="element-3948">
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide" data-id="element-3949">Parentesco</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3950">
                            {selectedPatient.emergencyContact?.relationship || <span className="text-gray-400 font-normal" data-id="element-3951">No registrado</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 px-4 py-3" data-id="element-3952">
                        <PhoneIcon className="w-4 h-4 text-red-400 flex-shrink-0" data-id="element-3953" />
                        <div className="min-w-0" data-id="element-3954">
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide" data-id="element-3955">Teléfono</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3956">
                            {selectedPatient.emergencyContact?.phone || <span className="text-gray-400 font-normal" data-id="element-3957">No registrado</span>}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel id="appointments" activeTab={activeTab} data-id="element-3958">
                <div className="space-y-4" data-id="element-3959">
                  {getAppointmentsByPatient(selectedPatient.id).length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-3960">
                      <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-3961" />
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-3962">
                        No hay citas registradas
                      </p>
                    </div> : getAppointmentsByPatient(selectedPatient.id).map(apt => {
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl hover:shadow-sm transition-shadow" data-id="element-3963">
                          <div className="flex items-center gap-4" data-id="element-3964">
                            <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold" data-id="element-3965">
                              {doctor?.name.charAt(0)}
                            </div>
                            <div data-id="element-3966">
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-3967">
                                {apt.date} - {formatTime(apt.time)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3968">
                                {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '-'}{' '}
                                • {service?.name}
                              </p>
                            </div>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-3969">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                </div>
              </TabPanel>

              {/* Registro de Visitas Tab */}
              <TabPanel id="visits" activeTab={activeTab} data-id="element-3970">
                {(() => {
              const patientVisits = getClinicalHistoriesByPatient(selectedPatient.id).filter(v => getClinicalHistoryStatus(v.id) === 'active').sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
              return <div className="space-y-4" data-id="element-3971">
                      {/* Summary Stats */}
                      {patientVisits.length > 0 && <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-3972">
                          <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30" data-id="element-3973">
                            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-id="element-3974">
                              {patientVisits.length}
                            </p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-500 font-medium" data-id="element-3975">
                              Visitas registradas
                            </p>
                          </div>
                          <div className="p-3 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/30" data-id="element-3976">
                            <p className="text-2xl font-bold text-teal-700 dark:text-teal-400" data-id="element-3977">
                              {new Set(patientVisits.map(v => v.doctorName)).size}
                            </p>
                            <p className="text-xs text-teal-600 dark:text-teal-500 font-medium" data-id="element-3978">
                              Doctores atendieron
                            </p>
                          </div>
                          <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/30" data-id="element-3979">
                            <p className="text-sm font-bold text-purple-700 dark:text-purple-400 truncate" data-id="element-3980">
                              {patientVisits[0] ? formatDate(patientVisits[0].date) : '-'}
                            </p>
                            <p className="text-xs text-purple-600 dark:text-purple-500 font-medium" data-id="element-3981">
                              Última visita
                            </p>
                          </div>
                        </div>}

                      {patientVisits.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-3982">
                          <ClipboardListIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-3983" />
                          <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-3984">
                            No hay registros de visitas
                          </p>
                          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-3985">
                            Los historiales clínicos creados para este paciente
                            aparecerán aquí
                          </p>
                        </div> : patientVisits.map((visit, index) => {
                  const isExpanded = expandedVisitId === visit.id;
                  return <div key={visit.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm" data-id="element-3986">
                              {/* Visit Header - Always visible */}
                              <button onClick={() => setExpandedVisitId(isExpanded ? null : visit.id)} className="w-full text-left p-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-3987">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm shadow-sm" data-id="element-3988">
                                  {index + 1}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-3989">
                                  <div className="flex items-center gap-2 mb-0.5" data-id="element-3990">
                                    <p className="font-semibold text-gray-900 dark:text-white text-sm" data-id="element-3991">
                                      {formatDate(visit.date)}
                                    </p>
                                    <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-3992"></span>
                                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3993">
                                      Dr. {visit.doctorName}
                                    </p>
                                  </div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-3994">
                                    {visit.personalBackground?.consultationReason ? `Motivo: ${visit.personalBackground.consultationReason}` : visit.diagnosis ? `Dx: ${visit.diagnosis.substring(0, 80)}${visit.diagnosis.length > 80 ? '...' : ''}` : 'Sin motivo registrado'}
                                  </p>
                                </div>
                                <div className="flex-shrink-0" data-id="element-3995">
                                  {isExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-3996" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-3997" />}
                                </div>
                              </button>

                              {/* Expanded Content */}
                              {isExpanded && <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-4 bg-gray-50/50 dark:bg-gray-900/30" data-id="element-3998">
                                  {/* Signos Vitales de esta visita */}
                                  {visit.physicalExam && (visit.physicalExam.bloodPressure || visit.physicalExam.heartRate || visit.physicalExam.bloodSugar || visit.physicalExam.temperature || visit.physicalExam.weight || visit.physicalExam.height) && <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/30 p-3" data-id="element-3999">
                                        <div className="flex items-center gap-2 mb-2" data-id="element-4000">
                                          <ActivityIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" data-id="element-4001" />
                                          <h4 className="text-xs font-semibold text-teal-700 dark:text-teal-300 uppercase tracking-wider" data-id="element-4002">
                                            Exploración Física
                                          </h4>
                                        </div>
                                        <div className="flex flex-wrap gap-3" data-id="element-4003">
                                          {visit.physicalExam.bloodPressure && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4004">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4005">
                                                P.A.
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4006">
                                                {visit.physicalExam.bloodPressure}
                                              </span>
                                            </div>}
                                          {visit.physicalExam.heartRate && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4007">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4008">
                                                F.C.
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4009">
                                                {visit.physicalExam.heartRate}{' '}
                                                bpm
                                              </span>
                                            </div>}
                                          {visit.physicalExam.bloodSugar && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4010">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4011">
                                                Glucosa
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4012">
                                                {visit.physicalExam.bloodSugar}{' '}
                                                mg/dL
                                              </span>
                                            </div>}
                                          {visit.physicalExam.temperature && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4013">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4014">
                                                Temp.
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4015">
                                                {visit.physicalExam.temperature}{' '}
                                                °C
                                              </span>
                                            </div>}
                                          {visit.physicalExam.weight && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4016">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4017">
                                                Peso
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4018">
                                                {visit.physicalExam.weight} kg
                                              </span>
                                            </div>}
                                          {visit.physicalExam.height && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4019">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4020">
                                                Altura
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4021">
                                                {visit.physicalExam.height} cm
                                              </span>
                                            </div>}
                                          {visit.physicalExam.lungs && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4022">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4023">
                                                Pulmones
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4024">
                                                {visit.physicalExam.lungs}
                                              </span>
                                            </div>}
                                          {visit.physicalExam.abdomen && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4025">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4026">
                                                Abdomen
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4027">
                                                {visit.physicalExam.abdomen}
                                              </span>
                                            </div>}
                                        </div>
                                      </div>}

                                  {/* Diagnóstico */}
                                  {visit.diagnosis && <div data-id="element-4028">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4029">
                                        <div className="w-6 h-6 rounded bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center" data-id="element-4030">
                                          <StethoscopeIcon className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" data-id="element-4031" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4032">
                                          Diagnóstico
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-4033">
                                        {visit.diagnosis}
                                      </p>
                                    </div>}

                                  {/* Tratamiento */}
                                  {visit.treatment && <div data-id="element-4034">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4035">
                                        <div className="w-6 h-6 rounded bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center" data-id="element-4036">
                                          <PillIcon className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" data-id="element-4037" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4038">
                                          Tratamiento
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-4039">
                                        {visit.treatment}
                                      </p>
                                    </div>}

                                  {/* Indicaciones Médicas */}
                                  {visit.medicalIndications && <div data-id="element-4040">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4041">
                                        <div className="w-6 h-6 rounded bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center" data-id="element-4042">
                                          <FileTextIcon className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" data-id="element-4043" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4044">
                                          Indicaciones Médicas
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-4045">
                                        {visit.medicalIndications}
                                      </p>
                                    </div>}

                                  {/* Sesiones */}
                                  {(visit.sessions || visit.sessionNumber) && <div data-id="element-4046">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4047">
                                        <div className="w-6 h-6 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-4048">
                                          <CalendarIcon className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" data-id="element-4049" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4050">
                                          Sesiones
                                        </h4>
                                      </div>
                                      <div className="pl-8 flex gap-4" data-id="element-4051">
                                        {visit.sessions && <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-4052">
                                            <span className="font-medium text-gray-500" data-id="element-4053">
                                              Sesiones:
                                            </span>{' '}
                                            {visit.sessions}
                                          </p>}
                                        {visit.sessionNumber && <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-4054">
                                            <span className="font-medium text-gray-500" data-id="element-4055">
                                              N° Sesión:
                                            </span>{' '}
                                            {visit.sessionNumber}
                                          </p>}
                                      </div>
                                    </div>}

                                  {/* Comentarios */}
                                  {visit.comments && <div data-id="element-4056">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4057">
                                        <div className="w-6 h-6 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center" data-id="element-4058">
                                          <FileTextIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" data-id="element-4059" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4060">
                                          Comentarios
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-4061">
                                        {visit.comments}
                                      </p>
                                    </div>}

                                  {/* Firma */}
                                  {visit.doctorSignature && <div className="pt-3 border-t border-gray-200 dark:border-gray-700 pl-8" data-id="element-4062">
                                      <p className="text-xs text-gray-400" data-id="element-4063">
                                        Firma:{' '}
                                        <span className="font-medium text-gray-600 dark:text-gray-300" data-id="element-4064">
                                          {visit.doctorSignature}
                                        </span>
                                      </p>
                                    </div>}
                                </div>}
                            </div>;
                })}
                    </div>;
            })()}
              </TabPanel>
            </Tabs>
          </div>}
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Paciente' : actionType === 'deactivate' ? 'Desactivar Paciente' : 'Eliminar Paciente'} size="sm" data-id="element-4065">
        <div className="space-y-4" data-id="element-4066">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-4067">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar a "${selectedPatient?.name}"? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar a "${selectedPatient?.name}"? No aparecerá en las listas activas.`}
            {actionType === 'delete' && `¿Estás seguro de que deseas eliminar permanentemente a "${selectedPatient?.name}"? Esta acción no se puede deshacer.`}
          </p>
          <div className="flex justify-end gap-3 modal-actions" data-id="element-4068">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-4069">
              Cancelar
            </Button>
            <Button variant={actionType === 'delete' ? 'danger' : 'primary'} onClick={handleAction} data-id="element-4070">
              {actionType === 'archive' ? 'Archivar' : actionType === 'deactivate' ? 'Desactivar' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Pacientes" isExporting={isExporting} data-id="element-4071" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Pacientes a PDF" isExporting={isPdfExporting} data-id="element-4072" />
    </div>;
}